import { Save, Settings } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Collapse,
  Divider,
  FormLabel,
  Stack,
  TextField,
} from "@mui/material";
import axios from "axios";
import {
  getCalendarJurisdictions,
  getCalendarSubdivisions,
  getCalendarTimezones,
  getOrganizationCalendar,
  putOrganizationCalendar,
} from "api";
import { getTranslation } from "common";
import ConfirmDialog from "components/dialogs/ConfirmDialog";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import {
  publishOrganizationCalendarRevision,
  useSnackbar,
  useTranslations,
} from "hooks";
import type {
  CalendarJurisdiction,
  CalendarMutationResult,
  CalendarSettingsUpdate,
  CalendarTimezone,
  OrganizationCalendar,
  WorkingInterval,
} from "model/organizationCalendarModel";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CalendarFieldLabel, {
  calendarDescriptionId,
} from "./CalendarFieldLabel";
import CalendarOverridesEditor from "./CalendarOverridesEditor";
import { validateWorkingIntervals } from "./calendarValidation";
import WorkingWeekEditor from "./WorkingWeekEditor";

interface OrganizationCalendarSettingsProps {
  organizationId: number;
}

interface CalendarDraft {
  timezoneId: string;
  countryCode: string;
  subdivisionCode: string | null;
  workingIntervals: WorkingInterval[];
}

const copyDraft = (calendar: OrganizationCalendar): CalendarDraft => ({
  timezoneId: calendar.timezoneId,
  countryCode: calendar.countryCode,
  subdivisionCode: calendar.subdivisionCode,
  workingIntervals: calendar.workingIntervals.map((interval) => ({
    ...interval,
  })),
});

const yearRange = (value: string) => {
  const year = Number(value);
  if (!Number.isInteger(year) || year < 1 || year > 9999) return null;
  const padded = String(year).padStart(4, "0");
  return { from: `${padded}-01-01`, to: `${padded}-12-31` };
};

const OrganizationCalendarSettings: React.FC<
  OrganizationCalendarSettingsProps
> = ({ organizationId }) => {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [server, setServer] = useState<OrganizationCalendar | null>(null);
  const [draft, setDraft] = useState<CalendarDraft | null>(null);
  const [timezones, setTimezones] = useState<CalendarTimezone[]>([]);
  const [jurisdictions, setJurisdictions] = useState<
    CalendarJurisdiction[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metadataError, setMetadataError] = useState(false);
  const [calendarError, setCalendarError] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadMetadata = useCallback(async () => {
    setMetadataLoading(true);
    try {
      const [timezoneRows, jurisdictionRows] = await Promise.all([
        getCalendarTimezones(),
        getCalendarJurisdictions(),
      ]);
      setTimezones(timezoneRows);
      setJurisdictions(jurisdictionRows);
      setMetadataError(false);
      return true;
    } catch {
      setMetadataError(true);
      return false;
    } finally {
      setMetadataLoading(false);
    }
  }, []);

  const loadCalendar = useCallback(async () => {
    const range = yearRange(year);
    if (!range) return false;
    setLoading(true);
    try {
      const next = await getOrganizationCalendar(
        organizationId,
        range.from,
        range.to
      );
      setServer({
        ...next,
        workingIntervals: next.workingIntervals.map((interval) => ({
          ...interval,
        })),
        days: next.days.map((day) => ({
          ...day,
          holidays: day.holidays.map((holiday) => ({ ...holiday })),
          effectiveIntervals: day.effectiveIntervals.map((interval) => ({
            ...interval,
          })),
          override: day.override
            ? {
                ...day.override,
                workingIntervals: day.override.workingIntervals.map(
                  (interval) => ({ ...interval })
                ),
              }
            : null,
        })),
      });
      setDraft(copyDraft(next));
      setConflict(false);
      setMutationError(null);
      setCalendarError(false);
      publishOrganizationCalendarRevision(
        organizationId,
        next.calendarRevision
      );
      return true;
    } catch {
      setCalendarError(true);
      return false;
    } finally {
      setLoading(false);
    }
  }, [organizationId, year]);

  useEffect(() => {
    void loadMetadata();
  }, [loadMetadata]);

  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar]);

  useEffect(() => {
    const countryCode = draft?.countryCode;
    if (!countryCode) return;
    let active = true;
    void getCalendarSubdivisions(countryCode)
      .then((items) => {
        if (!active) return;
        setDraft((current) =>
          current && current.countryCode === countryCode
            ? {
                ...current,
                subdivisionCode: items.some(
                  (item: any) =>
                    item.subdivisionCode === current.subdivisionCode
                )
                  ? current.subdivisionCode
                  : null,
              }
            : current
        );
      })
      .catch(() => {
        if (active) setMetadataError(true);
      });
    return () => {
      active = false;
    };
  }, [draft?.countryCode]);
  const localizedJurisdictions = useMemo(() => {
    let displayNames: Intl.DisplayNames | null = null;
    try {
      displayNames = new Intl.DisplayNames(navigator.languages, {
        type: "region",
      });
    } catch {
      displayNames = null;
    }
    return jurisdictions.map((jurisdiction) => ({
      ...jurisdiction,
      displayLabel:
        displayNames?.of(jurisdiction.countryCode) || jurisdiction.label,
    }));
  }, [jurisdictions]);

  const intervalErrors = draft
    ? validateWorkingIntervals(draft.workingIntervals)
    : [];
  const validationKeys = [
    ...(!draft?.timezoneId
      ? ["calendar.validation.timezone_required"]
      : []),
    ...(!draft?.countryCode
      ? ["calendar.validation.country_required"]
      : []),
    ...intervalErrors.map((error) => error.messageKey),
  ];
  const uniqueValidationKeys = Array.from(new Set(validationKeys));
  const localizationChanged = Boolean(
    server &&
      draft &&
      (server.timezoneId !== draft.timezoneId ||
        server.countryCode !== draft.countryCode ||
        server.subdivisionCode !== draft.subdivisionCode)
  );
  const dirty = Boolean(
    server &&
      draft &&
      (localizationChanged ||
        JSON.stringify(server.workingIntervals) !==
          JSON.stringify(draft.workingIntervals))
  );
  const loadError = metadataError || calendarError;
  const disabled = loading || metadataLoading || saving || readOnly;

  const runMutation = useCallback(
    async (
      operation: () => Promise<CalendarMutationResult>
    ): Promise<boolean> => {
      setSaving(true);
      setMutationError(null);
      try {
        const result = await operation();
        publishOrganizationCalendarRevision(
          organizationId,
          result.calendarRevision
        );
        sendSnack({
          message: getTranslation(
            translations,
            "calendar.settings.saved"
          ),
          type: "success",
        });
        await loadCalendar();
        return true;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 409) {
            setConflict(true);
            return false;
          }
          if (error.response?.status === 403) setReadOnly(true);
          const detail = error.response?.data?.detail;
          setMutationError(
            typeof detail?.code === "string"
              ? detail.code
              : "calendar.settings.save_error"
          );
        } else {
          setMutationError("calendar.settings.save_error");
        }
        return false;
      } finally {
        setSaving(false);
      }
    },
    [loadCalendar, organizationId, sendSnack, translations]
  );

  const persistSettings = async () => {
    if (!draft || !server || uniqueValidationKeys.length > 0) return;
    const body: CalendarSettingsUpdate = {
      timezoneId: draft.timezoneId,
      countryCode: draft.countryCode,
      subdivisionCode: draft.subdivisionCode,
      workingIntervals: draft.workingIntervals.map(
        ({ isoWeekday, startsAt, endsAt }) => ({
          isoWeekday,
          startsAt,
          endsAt,
        })
      ),
      expectedCalendarRevision: server.calendarRevision,
    };
    await runMutation(() => putOrganizationCalendar(organizationId, body));
  };

  const retry = async () => {
    await Promise.all([loadMetadata(), loadCalendar()]);
  };

  const timezone = timezones.find(
    (item) => item.timezoneId === draft?.timezoneId
  );
  const country = localizedJurisdictions.find(
    (item) => item.countryCode === draft?.countryCode
  );

  return (
    <Box
      component="fieldset"
      data-testid="organization-calendar-settings"
      data-calendar-revision={String(server?.calendarRevision || "")}
      sx={{
        borderRadius: 2,
        borderColor: "divider",
        borderWidth: 1,
        px: 3,
        py: 2,
      }}
    >
      <FormLabel
        component="legend"
        sx={{ px: 1, fontWeight: 800, letterSpacing: 1 }}
      >
        {getTranslation(translations, "calendar.settings.title")}
      </FormLabel>

      {loadError && (
        <Alert
          severity="error"
          action={
            <FlowentiButton size="small" onClick={() => void retry()}>
              {getTranslation(translations, "calendar.settings.retry")}
            </FlowentiButton>
          }
          sx={{ mb: 2 }}
        >
          {getTranslation(translations, "calendar.settings.load_error")}
        </Alert>
      )}

      {loading && !server ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : (
        server &&
        draft && (
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="flex-end">
              <FlowentiButton
                variant="outlined"
                size="small"
                startIcon={<Settings />}
                data-testid="calendar-settings-toggle"
                aria-expanded={settingsOpen}
                aria-controls="calendar-localization-settings"
                onClick={() => setSettingsOpen((open) => !open)}
              >
                {getTranslation(translations, "settings.name")}
              </FlowentiButton>
            </Stack>
            <Collapse in={settingsOpen} id="calendar-localization-settings">
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
              >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CalendarFieldLabel
                  fieldId="timezone"
                  label={getTranslation(
                    translations,
                    "calendar.timezone.label"
                  )}
                  help={getTranslation(
                    translations,
                    "calendar.timezone.help"
                  )}
                />
                <Autocomplete
                  id="calendar-timezone"
                  options={timezones}
                  value={timezone || null}
                  disabled={disabled}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.timezoneId === value.timezoneId
                  }
                  onChange={(_, value) =>
                    setDraft((current) =>
                      current
                        ? {
                            ...current,
                            timezoneId: value?.timezoneId || "",
                          }
                        : current
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      inputProps={{
                        ...params.inputProps,
                        "aria-label": getTranslation(
                          translations,
                          "calendar.timezone.label"
                        ),
                        "aria-describedby":
                          calendarDescriptionId("timezone"),
                        "data-calendar-field": "true",
                        "data-calendar-field-id": "timezone",
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CalendarFieldLabel
                  fieldId="country"
                  label={getTranslation(
                    translations,
                    "calendar.country.label"
                  )}
                  help={getTranslation(
                    translations,
                    "calendar.country.help"
                  )}
                />
                <Autocomplete
                  id="calendar-country"
                  options={localizedJurisdictions}
                  value={country || null}
                  disabled={disabled}
                  getOptionLabel={(option) => option.displayLabel}
                  isOptionEqualToValue={(option, value) =>
                    option.countryCode === value.countryCode
                  }
                  onChange={(_, value) =>
                    setDraft((current) =>
                      current
                        ? {
                            ...current,
                            countryCode: value?.countryCode || "",
                            subdivisionCode: null,
                          }
                        : current
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      inputProps={{
                        ...params.inputProps,
                        "aria-label": getTranslation(
                          translations,
                          "calendar.country.label"
                        ),
                        "aria-describedby":
                          calendarDescriptionId("country"),
                        "data-calendar-field": "true",
                        "data-calendar-field-id": "country",
                      }}
                    />
                  )}
                />
              </Box>
              </Stack>
            </Collapse>

            <WorkingWeekEditor
              intervals={draft.workingIntervals}
              disabled={disabled}
              onChange={(workingIntervals) =>
                setDraft((current) =>
                  current ? { ...current, workingIntervals } : current
                )
              }
            />

            {uniqueValidationKeys.length > 0 && (
              <Alert
                severity="error"
                data-testid="calendar-validation-errors"
              >
                <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                  {uniqueValidationKeys.map((key) => (
                    <Box component="li" key={key}>
                      {getTranslation(translations, key)}
                    </Box>
                  ))}
                </Stack>
              </Alert>
            )}

            {mutationError && (
              <Alert severity="error">
                {getTranslation(translations, mutationError)}
              </Alert>
            )}
            {conflict && (
              <Alert severity="warning" data-testid="calendar-conflict">
                <Stack spacing={1}>
                  {getTranslation(
                    translations,
                    "calendar.settings.conflict.body"
                  )}
                  <FlowentiButton
                    size="small"
                    variant="outlined"
                    data-testid="calendar-reload-server"
                    onClick={() => void loadCalendar()}
                  >
                    {getTranslation(
                      translations,
                      "calendar.settings.conflict.reload"
                    )}
                  </FlowentiButton>
                </Stack>
              </Alert>
            )}

            <FlowentiButton
              variant="contained"
              startIcon={<Save />}
              data-testid="calendar-save"
              disabled={
                disabled ||
                !dirty ||
                uniqueValidationKeys.length > 0 ||
                conflict
              }
              onClick={() => {
                if (localizationChanged) setConfirmOpen(true);
                else void persistSettings();
              }}
            >
              {getTranslation(translations, "calendar.settings.save")}
            </FlowentiButton>

            <Divider />
            <CalendarOverridesEditor
              organizationId={organizationId}
              calendar={server}
              year={year}
              onYearChange={setYear}
              disabled={disabled || conflict}
              runMutation={runMutation}
            />
          </Stack>
        )
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={getTranslation(
          translations,
          "calendar.settings.reconciliation_warning.title"
        )}
        content={getTranslation(
          translations,
          "calendar.settings.reconciliation_warning.body"
        )}
        cancelText={getTranslation(
          translations,
          "user.buttoncancel.label"
        )}
        confirmText={getTranslation(
          translations,
          "calendar.settings.save"
        )}
        onConfirm={() => {
          setConfirmOpen(false);
          void persistSettings();
        }}
      />
    </Box>
  );
};

export default OrganizationCalendarSettings;
